'use client';

import React, { useState, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { useRouter } from 'next/navigation';
import useEcomerce from '~/hooks/useEcomerce';
import NotifyAvailabilityModal from '~/components/elements/products/modules/NotifyAvailabilityModal';
import { useLoginPrompt, isGuestUser } from '~/components/elements/products/modules/LoginPromptModal';
import { useGlobalPopup } from '~/components/GlobalPopupProvider';
import { getAvailability, getAddToCartLabel } from '~/utilities/availability'; // NEU

const ICONS_BASE = `${process.env.NEXT_PUBLIC_FRONTEND_URL}/static/img/icons`;

const ModuleDetailShoppingActions = ({
  ecomerce,
  product,
  selectedVariant = null,
  extended = false,
}) => {
  const [quantity, setQuantity] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const Router = useRouter();
  const { addItem, removeItem } = useEcomerce();
  const { showPopup } = useGlobalPopup();
  const { openLoginPrompt, loginPromptModal } = useLoginPrompt();

  const activeProduct = selectedVariant || product;

  const activeProductId =
    selectedVariant?.id ||
    selectedVariant?.data?.id ||
    product?.id ||
    product?.data?.id ||
    product?.attributes?.id ||
    null;

  const activeProductTitle =
    selectedVariant?.attributes?.title ||
    selectedVariant?.title ||
    product?.attributes?.title ||
    product?.title ||
    'Produkt';

  // GEÄNDERT: Verfügbarkeit über getAvailability (inkl. Vorbestellung)
  const activeProductAttrs = activeProduct?.attributes || activeProduct || {};
  const shippingAttrs =
    activeProductAttrs.shippings?.data?.attributes ||
    product?.attributes?.shippings?.data?.attributes ||
    null;
  const availability = useMemo(
    () => getAvailability(activeProductAttrs, shippingAttrs),
    [activeProductAttrs, shippingAttrs]
  );
  const isAvailable = availability.canOrder;

  const activeProductPrice = useMemo(() => {
    if (activeProductAttrs?.price != null) return activeProductAttrs.price;
    return null;
  }, [activeProductAttrs]);

  const [isHeartSpinning, setIsHeartSpinning] = useState(false);
  const [isCompareSpinning, setIsCompareSpinning] = useState(false);

  const [showNotifyModal, setShowNotifyModal] = useState(false);

  const normalizeItemId = useCallback((item) => {
    return (
      item?.id ||
      item?.productId ||
      item?.product?.id ||
      item?.data?.id ||
      item?.attributes?.id ||
      item?.product?.data?.id ||
      item?.product?.attributes?.id ||
      null
    );
  }, []);

  const isInWishlist = useMemo(() => {
    if (!activeProductId || !Array.isArray(ecomerce?.wishlistItems)) return false;
    return ecomerce.wishlistItems.some(
      (item) => String(normalizeItemId(item)) === String(activeProductId)
    );
  }, [activeProductId, ecomerce?.wishlistItems, normalizeItemId]);

  const isInCompare = useMemo(() => {
    if (!activeProductId || !Array.isArray(ecomerce?.compareItems)) return false;
    return ecomerce.compareItems.some(
      (item) => String(normalizeItemId(item)) === String(activeProductId)
    );
  }, [activeProductId, ecomerce?.compareItems, normalizeItemId]);

  const shoppingCartIcon = (
    <span
      className="msa-icon-mask msa-icon-cart"
      style={{ width: 20, height: 20, color: '#ffffff' }}
      aria-hidden="true"
    />
  );

  const bellIcon = (
    <span
      className="msa-icon-mask msa-icon-bell"
      style={{ width: 18, height: 18, color: '#ffffff' }}
      aria-hidden="true"
    />
  );

  const MinusIcon = (
    <span className="msa-icon-mask msa-icon-minus" style={{ width: 24, height: 24 }} aria-hidden="true" />
  );

  const PlusIcon = (
    <span className="msa-icon-mask msa-icon-plus" style={{ width: 24, height: 24 }} aria-hidden="true" />
  );

  const WishlistIcon = (
    <span className={`msa-icon-wrapper${isHeartSpinning ? ' spinning' : ''}`}>
      <span
        className={`msa-icon-mask msa-icon-heart${isInWishlist ? ' is-active' : ''}`}
        style={{ width: 36, height: 36 }}
        aria-hidden="true"
      />
    </span>
  );

  const CompareIcon = (
    <span className={`msa-icon-wrapper${isCompareSpinning ? ' spinning' : ''}`}>
      <span
        className={`msa-icon-mask msa-icon-compare${isInCompare ? ' is-active' : ''}`}
        style={{ width: 36, height: 36 }}
        aria-hidden="true"
      />
    </span>
  );

  const IconStyles = (
    <style jsx global>{`
      .msa-icon-mask {
        position: relative;
        display: inline-block;
        color: inherit;
      }
      .msa-icon-mask::before {
        content: '';
        position: absolute;
        inset: 0;
        background-color: currentColor;
        -webkit-mask-repeat: no-repeat;
        mask-repeat: no-repeat;
        -webkit-mask-position: center;
        mask-position: center;
        -webkit-mask-size: contain;
        mask-size: contain;
      }
      .msa-icon-heart::after,
      .msa-icon-compare::after {
        content: '';
        position: absolute;
        inset: 0;
        background-color: currentColor;
        -webkit-mask-repeat: no-repeat;
        mask-repeat: no-repeat;
        -webkit-mask-position: center;
        mask-position: center;
        -webkit-mask-size: contain;
        mask-size: contain;
        opacity: 0;
        transition: opacity 0.3s ease;
      }
      .msa-icon-cart::before {
        -webkit-mask-image: url(${ICONS_BASE}/cart-icon-mask.svg);
        mask-image: url(${ICONS_BASE}/cart-icon-mask.svg);
      }
      .msa-icon-bell::before {
        -webkit-mask-image: url(${ICONS_BASE}/bell-icon-mask.svg);
        mask-image: url(${ICONS_BASE}/bell-icon-mask.svg);
      }
      .msa-icon-minus::before {
        -webkit-mask-image: url(${ICONS_BASE}/minus-icon.svg);
        mask-image: url(${ICONS_BASE}/minus-icon.svg);
      }
      .msa-icon-plus::before {
        -webkit-mask-image: url(${ICONS_BASE}/plus-icon.svg);
        mask-image: url(${ICONS_BASE}/plus-icon.svg);
      }
      .msa-icon-heart {
        color: #666;
      }
      .msa-icon-heart::before {
        -webkit-mask-image: url(${ICONS_BASE}/heart-outline.svg);
        mask-image: url(${ICONS_BASE}/heart-outline.svg);
      }
      .msa-icon-heart::after {
        -webkit-mask-image: url(${ICONS_BASE}/heart-fill.svg);
        mask-image: url(${ICONS_BASE}/heart-fill.svg);
      }
      .msa-icon-heart.is-active {
        color: #06c;
      }
      .msa-icon-heart.is-active::after {
        opacity: 1;
      }

      .msa-icon-compare {
        color: #666;
      }
      .msa-icon-compare::before {
        -webkit-mask-image: url(${ICONS_BASE}/compare-outline.svg);
        mask-image: url(${ICONS_BASE}/compare-outline.svg);
      }
      .msa-icon-compare::after {
        -webkit-mask-image: url(${ICONS_BASE}/compare-fill.svg);
        mask-image: url(${ICONS_BASE}/compare-fill.svg);
      }
      .msa-icon-compare.is-active {
        color: #06c;
      }
      .msa-icon-compare.is-active::after {
        opacity: 1;
      }

      .msa-icon-wrapper {
        display: flex;
        perspective: 200px;
      }
      .msa-icon-wrapper.spinning {
        animation: msaIconFlip 0.45s cubic-bezier(0.4, 0, 0.2, 1) forwards;
      }
      @keyframes msaIconFlip {
        0% {
          transform: rotateY(0deg);
        }
        50% {
          transform: rotateY(90deg);
        }
        100% {
          transform: rotateY(0deg);
        }
      }
    `}</style>
  );

  async function handleAddItemToCart(e) {
    e.preventDefault();
    if (!activeProductId) {
      showPopup({
        type: 'error',
        title: 'Fehler',
        detail: 'Kein gültiges Produkt oder keine Variante ausgewählt.',
      });
      return;
    }

    setIsProcessing(true);

    try {
      await addItem({ id: activeProductId, quantity }, ecomerce.cartItems, 'cart');
      showPopup({
        type: 'success',
        title: 'Warenkorb aktualisiert',
        detail: `${activeProductTitle} wurde dem Warenkorb hinzugefügt.`,
      });
    } catch (err) {
      showPopup({
        type: 'error',
        title: 'Fehler',
        detail: 'Produkt konnte nicht hinzugefügt werden.',
      });
    } finally {
      setTimeout(() => setIsProcessing(false), 1000);
    }
  }

  async function handleBuynow(e) {
    e.preventDefault();
    if (!activeProductId) {
      showPopup({
        type: 'error',
        title: 'Fehler',
        detail: 'Bitte zuerst eine Variante auswählen.',
      });
      return;
    }

    setIsProcessing(true);

    try {
      await addItem({ id: activeProductId, quantity }, ecomerce.cartItems, 'cart');
      showPopup({
        type: 'success',
        title: 'Zum Checkout',
        detail: 'Produkt wurde in den Warenkorb gelegt. Weiterleitung zum Checkout...',
      });
      setTimeout(() => Router.push('/account/shopping-cart'), 1000);
    } catch (err) {
      showPopup({
        type: 'error',
        title: 'Fehler',
        detail: 'Produkt konnte nicht hinzugefügt werden.',
      });
      setIsProcessing(false);
    }
  }

  const openNotifyModal = useCallback((e) => {
    e.preventDefault();
    setShowNotifyModal(true);
  }, []);

  const closeNotifyModal = useCallback(() => {
    setShowNotifyModal(false);
  }, []);

  const handleToggleCompare = async (e) => {
    e.preventDefault();

    if (!activeProductId) {
      showPopup({
        type: 'error',
        title: 'Fehler',
        detail: 'Kein gültiges Produkt zum Vergleichen.',
      });
      return;
    }

    setIsCompareSpinning(true);
    setTimeout(() => setIsCompareSpinning(false), 450);

    try {
      if (isInCompare) {
        if (typeof removeItem === 'function') {
          await removeItem({ id: activeProductId }, ecomerce.compareItems, 'compare');
        } else {
          throw new Error('remove_not_available');
        }

        showPopup({
          type: 'info',
          title: 'Entfernt',
          detail: 'Dieses Produkt wurde aus der Vergleichsliste entfernt.',
        });
      } else {
        await addItem({ id: activeProductId }, ecomerce.compareItems, 'compare');
        showPopup({
          type: 'success',
          title: 'Erfolg!',
          detail: 'Dieses Produkt wurde zur Vergleichsliste hinzugefügt!',
        });
      }
    } catch (err) {
      if (err?.message?.startsWith('already_in_')) {
        showPopup({
          type: 'info',
          title: 'Bereits vorhanden',
          detail: err.message.split(':')[1] || 'Bereits in der Liste vorhanden.',
        });
      } else {
        showPopup({
          type: 'error',
          title: 'Fehler',
          detail:
            typeof removeItem === 'function'
              ? 'Produkt konnte nicht in der Vergleichsliste geändert werden.'
              : 'removeItem fehlt im useEcomerce Hook.',
        });
      }
    }
  };

  const handleToggleWishlist = async (e) => {
    e.preventDefault();

    if (!activeProductId) {
      showPopup({
        type: 'error',
        title: 'Fehler',
        detail: 'Kein gültiges Produkt für die Merkliste.',
      });
      return;
    }

    setIsHeartSpinning(true);
    setTimeout(() => setIsHeartSpinning(false), 450);

    try {
      if (isInWishlist) {
        if (typeof removeItem === 'function') {
          await removeItem({ id: activeProductId }, ecomerce.wishlistItems, 'wishlist');
        } else {
          throw new Error('remove_not_available');
        }

        showPopup({
          type: 'info',
          title: 'Entfernt',
          detail: 'Dieser Artikel wurde aus Ihrer Merkliste entfernt.',
        });
      } else {
        const wasGuest = isGuestUser();
        await addItem({ id: activeProductId }, ecomerce.wishlistItems, 'wishlist');

        if (wasGuest) {
          // Gast: statt Erfolgs-Toast das Login-Popup zeigen
          openLoginPrompt();
        } else {
          showPopup({
            type: 'success',
            title: 'Erfolg!',
            detail: 'Dieser Artikel wurde Ihrer Merkliste hinzugefügt.',
          });
        }
      }
    } catch (err) {
      if (err?.message?.startsWith('already_in_')) {
        showPopup({
          type: 'info',
          title: 'Bereits vorhanden',
          detail: err.message.split(':')[1] || 'Bereits in der Liste vorhanden.',
        });
      } else {
        showPopup({
          type: 'error',
          title: 'Fehler',
          detail:
            typeof removeItem === 'function'
              ? 'Produkt konnte nicht in der Merkliste geändert werden.'
              : 'removeItem fehlt im useEcomerce Hook.',
        });
      }
    }
  };

  function handleIncreaseItemQty(e) {
    e.preventDefault();
    setQuantity((prev) => prev + 1);
  }

  function handleDecreaseItemQty(e) {
    e.preventDefault();
    setQuantity((prev) => (prev > 1 ? prev - 1 : 1));
  }

  // GEÄNDERT: Button-Text je nach Status ("Jetzt vorbestellen" / "In den Warenkorb")
  const buttonText = isProcessing ? 'Wird hinzugefügt...' : getAddToCartLabel(availability);
  const buttonClass = isProcessing ? 'ps-btn--loading' : '';

  if (!extended) {
    return (
      <>
        <div className="ps-product__shopping">
          <div className="shopping-figure">
            <div className="shopping-row">
              {isAvailable ? (
                <>
                  <div className="quantity-selector">
                    <button
                      className="quantity-btn quantity-btn--minus"
                      onClick={handleDecreaseItemQty}
                      disabled={quantity <= 1}
                      aria-label="Menge verringern"
                    >
                      {MinusIcon}
                    </button>

                    <input
                      className="quantity-input"
                      type="number"
                      min="1"
                      max="Infinity"
                      step="1"
                      value={quantity}
                      readOnly
                    />

                    <button
                      className="quantity-btn quantity-btn--plus"
                      onClick={handleIncreaseItemQty}
                      aria-label="Menge erhöhen"
                    >
                      {PlusIcon}
                    </button>
                  </div>

                  <a
                    className={`ps-btn ps-btn--black cart-btn ${buttonClass}`}
                    href="#"
                    onClick={handleAddItemToCart}
                  >
                    <span className="mobile-icon-wrapper">{shoppingCartIcon}</span>
                    <span className="mobile-text">{buttonText}</span>
                  </a>
                </>
              ) : (
                <a
                  className="ps-btn ps-btn--black notify-btn"
                  href="#"
                  onClick={openNotifyModal}
                >
                  <span className="mobile-icon-wrapper">{bellIcon}</span>
                  <span className="mobile-text">Informiere mich...</span>
                </a>
              )}

              <div className="ps-product__actionsps-product__actions product-actions-inline">
                <a
                  href="#"
                  onClick={handleToggleWishlist}
                  aria-label={isInWishlist ? 'Aus Wunschliste entfernen' : 'Zur Wunschliste hinzufügen'}
                  className={isInWishlist ? 'is-active is-wishlist-active' : ''}
                >
                  {WishlistIcon}
                </a>

                <a
                  href="#"
                  onClick={handleToggleCompare}
                  aria-label={isInCompare ? 'Aus Vergleichsliste entfernen' : 'Zur Vergleichsliste hinzufügen'}
                  className={isInCompare ? 'is-active is-compare-active' : ''}
                >
                  {CompareIcon}
                </a>
              </div>
            </div>
          </div>

          <style jsx global>{`
            .ps-product__shopping {
              width: 100%;
              margin-top: 16px;
            }
            .shopping-figure {
              width: 100%;
            }
            .desktop-cart-btn.ps-btn--black::after,
            .mobile-cart-btn.ps-btn--black::after,
            .cart-btn.ps-btn--black::after,
            .notify-btn.ps-btn--black::after,
            .desktop-cart-btn.ps-btn--black:hover::after,
            .mobile-cart-btn.ps-btn--black:hover::after,
            .cart-btn.ps-btn--black:hover::after,
            .notify-btn.ps-btn--black:hover::after {
              display: none !important;
            }
            .shopping-row {
              display: flex !important;
              align-items: center;
              gap: 12px;
              width: 100%;
              flex-wrap: nowrap;
              box-sizing: border-box;
            }
            .quantity-selector {
              display: flex;
              height: 44px;
              gap: 0;
              min-width: 0 !important;
              max-width: none !important;
              box-sizing: border-box;
              font-size: 16px;
              font-weight: 700;
            }
            .quantity-btn {
              flex: 1 1 40px;
              width: auto;
              min-width: 0;
              height: 44px;
              display: flex;
              justify-content: center;
              align-items: center;
              border: 1px solid rgb(191, 195, 205);
              background: rgb(255, 255, 255);
              cursor: pointer;
              appearance: button;
              margin: 0;
              padding: 0;
              box-sizing: border-box;
              transition-property: color, background-color, border-color;
              transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
              transition-duration: 0.15s;
              user-select: none;
              font-size: 16px;
              font-weight: 700;
              line-height: 24px;
            }
            .quantity-btn--minus {
              border-top-left-radius: 4px;
              border-bottom-left-radius: 4px;
              border-right-width: 0px;
            }
            .quantity-btn--plus {
              border-top-right-radius: 4px;
              border-bottom-right-radius: 4px;
              border-left-width: 0px;
            }
            .quantity-btn--minus:disabled {
              color: rgb(139, 145, 161);
              background-color: rgb(236, 237, 240);
              cursor: not-allowed;
            }
            .quantity-btn:hover:not(:disabled) {
              background-color: rgb(248, 249, 250);
            }
            .quantity-btn svg,
            .quantity-btn .msa-icon-mask {
              width: 14px;
              height: 14px;
              flex-shrink: 0;
            }
            .quantity-input {
              flex: 1 1 40px;
              width: auto;
              min-width: 0;
              height: 44px;
              border: 1px solid rgb(191, 195, 205);
              border-left: none;
              border-right: none;
              background-color: rgb(255, 255, 255);
              color: rgb(12, 25, 58);
              text-align: center;
              appearance: textfield;
              margin: 0;
              padding: 0;
              box-sizing: border-box;
              font-size: 16px;
              font-weight: 700;
              line-height: 24px;
            }
            .cart-btn {
              display: flex !important;
              align-items: center;
              justify-content: center;
              flex: 0 0 calc(50% - 12px) !important;
              width: calc(50% - 12px) !important;
              min-width: 0 !important;
              max-width: none !important;
              height: 44px;
              padding: 0 14px;
              white-space: nowrap;
              overflow: hidden;
              box-sizing: border-box;
            }
            .notify-btn {
              display: flex !important;
              align-items: center;
              justify-content: center;
              flex: 1 1 auto !important;
              min-width: 0 !important;
              max-width: none !important;
              height: 44px;
              padding: 0 14px;
              white-space: nowrap;
              overflow: hidden;
              box-sizing: border-box;
            }
            .ps-btn--loading {
              position: relative;
              opacity: 0.7;
              pointer-events: none;
              transition: opacity 0.2s ease !important;
              overflow: hidden;
            }
            .ps-btn--loading::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              width: 0;
              height: 100%;
              background: rgba(255, 255, 255, 0.2);
              z-index: 1;
              animation: slideRight 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
            }
            .ps-btn--loading::after {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 2px;
              background: rgba(255, 255, 255, 0.5);
              transform: translateX(-100%);
              z-index: 2;
              animation: lineSlideRight 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s forwards;
            }
            @keyframes slideRight {
              to {
                width: 100%;
              }
            }
            @keyframes lineSlideRight {
              to {
                transform: translateX(0);
              }
            }
            .mobile-icon-wrapper {
              position: relative;
              z-index: 3;
              display: flex;
              align-items: center;
              justify-content: center;
              min-width: 20px;
              flex: 0 0 auto;
              margin-right: 8px;
            }
            .mobile-icon-wrapper svg,
            .mobile-icon-wrapper .msa-icon-mask {
              width: 20px !important;
              height: 20px !important;
            }
            .mobile-text {
              position: relative;
              z-index: 3;
              display: block;
              min-width: 0;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              line-height: 1.2;
              font-size: 14px;
            }
            .product-actions-inline {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              flex: 0 0 calc(25% - 6px) !important;
              width: calc(25% - 6px) !important;
              min-width: 0 !important;
              max-width: none !important;
              flex-wrap: nowrap;
              box-sizing: border-box;
            }
            .product-actions-inline a {
              flex: 1 1 0;
              min-width: 0;
              width: auto;
              height: 44px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              color: inherit;
              -webkit-tap-highlight-color: transparent;
            }
            .product-actions-inline a:hover,
            .product-actions-inline a:focus,
            .product-actions-inline a:active,
            .product-actions-inline a:visited {
              color: inherit;
            }
            @media (max-width: 768px) {
              .shopping-row {
                gap: 8px;
              }
              .quantity-selector {
                flex: 0 0 auto !important;
                width: auto !important;
              }
              .quantity-btn {
                flex: 0 0 28px;
                width: 28px;
              }
              .quantity-input {
                flex: 0 0 30px;
                width: 30px;
              }
              .cart-btn {
                flex: 1 1 auto !important;
                width: auto !important;
                min-width: 0 !important;
                max-width: none !important;
                padding: 0 6px;
              }
              .cart-btn .mobile-icon-wrapper,
              .notify-btn .mobile-icon-wrapper {
                display: none;
              }
              .cart-btn,
              .notify-btn {
                overflow: visible;
                white-space: nowrap;
              }
              .mobile-text {
                font-size: 12px;
                white-space: nowrap;
                overflow: visible;
                text-overflow: clip;
              }
              .product-actions-inline {
                gap: 4px;
                flex: 0 0 auto !important;
                width: auto !important;
              }
              .product-actions-inline a {
                flex: 0 0 auto;
                width: auto;
              }
              .product-actions-inline a .msa-icon-mask {
                width: 28px !important;
                height: 28px !important;
              }
            }
            @media (max-width: 420px) {
              .shopping-row {
                gap: 6px;
              }
              .quantity-btn {
                flex: 0 0 26px;
                width: 26px;
              }
              .quantity-input {
                flex: 0 0 28px;
                width: 28px;
              }
              .cart-btn {
                padding: 0 4px;
              }
              .mobile-icon-wrapper {
                margin-right: 6px;
              }
              .mobile-text {
                font-size: 11px;
              }
            }
          `}</style>
          {IconStyles}
        </div>
        {showNotifyModal && (
          <NotifyAvailabilityModal
            productId={activeProductId}
            productPrice={activeProductPrice}
            onClose={closeNotifyModal}
          />
        )}
        {loginPromptModal}
      </>
    );
  }

  return (
    <>
      <div className="ps-product__shopping extend">
        <div className="ps-product__btn-group">
          {isAvailable ? (
            <>
              <div className="qv-figure">
                <div className="quantity-selector">
                  <button
                    className="quantity-btn quantity-btn--minus"
                    onClick={handleDecreaseItemQty}
                    disabled={quantity <= 1}
                    aria-label="Menge verringern"
                  >
                    {MinusIcon}
                  </button>

                  <input
                    className="quantity-input"
                    type="number"
                    min="1"
                    max="Infinity"
                    step="1"
                    value={quantity}
                    readOnly
                  />

                  <button
                    className="quantity-btn quantity-btn--plus"
                    onClick={handleIncreaseItemQty}
                    aria-label="Menge erhöhen"
                  >
                    {PlusIcon}
                  </button>
                </div>
              </div>

              <a
                className={`ps-btn ps-btn--black qv-cart-btn ${buttonClass}`}
                href="#"
                onClick={handleAddItemToCart}
              >
                <span className="mobile-icon-wrapper">{shoppingCartIcon}</span>
                <span className="mobile-text">{buttonText}</span>
              </a>
            </>
          ) : (
            <a
              className="ps-btn ps-btn--black qv-notify-btn"
              href="#"
              onClick={openNotifyModal}
            >
              <span className="mobile-icon-wrapper">{bellIcon}</span>
              <span className="mobile-text">Informiere mich...</span>
            </a>
          )}

          <div className="ps-product__actionsps-product__actions qv-actions">
            <a
              href="#"
              onClick={handleToggleWishlist}
              aria-label={isInWishlist ? 'Aus Wunschliste entfernen' : 'Zur Wunschliste hinzufügen'}
              className={isInWishlist ? 'is-active is-wishlist-active' : ''}
            >
              {WishlistIcon}
            </a>
            <a
              href="#"
              onClick={handleToggleCompare}
              aria-label={isInCompare ? 'Aus Vergleichsliste entfernen' : 'Zur Vergleichsliste hinzufügen'}
              className={isInCompare ? 'is-active is-compare-active' : ''}
            >
              {CompareIcon}
            </a>
          </div>
        </div>

        {isAvailable && (
          <a className="ps-btn qv-buynow-btn" href="#" onClick={handleBuynow}>
            Jetzt kaufen
          </a>
        )}

        <style jsx>{`
          .ps-product__shopping.extend {
            width: 100%;
            max-width: 100%;
            display: block !important;
            box-sizing: border-box;
            overflow: hidden;
            margin-top: 16px;
          }
          .ps-product__btn-group {
            display: flex !important;
            align-items: center;
            gap: 8px;
            flex-wrap: nowrap;
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
          }
          .qv-figure {
            flex: 0 0 calc(25% - 4px) !important;
            width: calc(25% - 4px) !important;
            min-width: 0 !important;
            max-width: none !important;
            margin: 0;
            box-sizing: border-box;
          }
          .qv-figure .quantity-selector {
            width: 100%;
            max-width: 100%;
          }
          .qv-cart-btn.ps-btn--black::after,
          .qv-cart-btn.ps-btn--black:hover::after,
          .qv-notify-btn.ps-btn--black::after,
          .qv-notify-btn.ps-btn--black:hover::after {
            display: none !important;
          }
          .qv-cart-btn {
            display: flex !important;
            align-items: center;
            justify-content: center;
            flex: 0 0 calc(50% - 8px) !important;
            width: calc(50% - 8px) !important;
            min-width: 0 !important;
            height: 44px;
            padding: 0 14px;
            white-space: nowrap;
            overflow: hidden;
            box-sizing: border-box;
          }
          .qv-notify-btn {
            display: flex !important;
            align-items: center;
            justify-content: center;
            flex: 1 1 auto !important;
            min-width: 0 !important;
            height: 44px;
            padding: 0 14px;
            white-space: nowrap;
            overflow: hidden;
            box-sizing: border-box;
          }
          .qv-actions {
            display: flex !important;
            align-items: center;
            justify-content: center;
            gap: 8px;
            flex: 0 0 calc(25% - 4px) !important;
            width: calc(25% - 4px) !important;
            min-width: 0 !important;
            flex-wrap: nowrap;
            box-sizing: border-box;
          }
          .qv-actions a {
            flex: 1 1 0;
            min-width: 0;
            height: 44px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            color: inherit;
            -webkit-tap-highlight-color: transparent;
          }
          .qv-actions a:hover,
          .qv-actions a:focus,
          .qv-actions a:active,
          .qv-actions a:visited {
            color: inherit;
          }
          .qv-buynow-btn {
            display: flex !important;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 44px;
            margin-top: 12px;
          }
          .quantity-selector {
            display: flex;
            height: 44px;
            gap: 0;
            width: 100%;
            font-size: 16px;
            font-weight: 700;
          }
          .quantity-btn {
            flex: 1 1 40px;
            width: auto;
            min-width: 0;
            height: 44px;
            display: flex;
            justify-content: center;
            align-items: center;
            border: 1px solid rgb(191, 195, 205);
            background: rgb(255, 255, 255);
            cursor: pointer;
            appearance: button;
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            transition-property: color, background-color, border-color;
            transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
            transition-duration: 0.15s;
            user-select: none;
            font-size: 16px;
            font-weight: 700;
            line-height: 24px;
            flex-shrink: 0;
          }
          .quantity-btn--minus {
            border-top-left-radius: 4px;
            border-bottom-left-radius: 4px;
            border-right-width: 0px;
          }
          .quantity-btn--plus {
            border-top-right-radius: 4px;
            border-bottom-right-radius: 4px;
            border-left-width: 0px;
          }
          .quantity-btn--minus:disabled {
            color: rgb(139, 145, 161);
            background-color: rgb(236, 237, 240);
            cursor: not-allowed;
          }
          .quantity-btn:hover:not(:disabled) {
            background-color: rgb(248, 249, 250);
          }
          .quantity-btn svg,
          .quantity-btn .msa-icon-mask {
            width: 24px;
            height: 24px;
            flex-shrink: 0;
          }
          .quantity-input {
            flex: 1 1 40px;
            width: auto;
            min-width: 0;
            height: 44px;
            border: 1px solid rgb(191, 195, 205);
            border-left: none;
            border-right: none;
            background-color: rgb(255, 255, 255);
            color: rgb(12, 25, 58);
            text-align: center;
            appearance: textfield;
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            font-size: 16px;
            font-weight: 700;
            line-height: 24px;
          }
          .mobile-icon-wrapper {
            position: relative;
            z-index: 3;
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 20px;
            flex: 0 0 auto;
            margin-right: 8px;
          }
          .mobile-icon-wrapper svg,
          .mobile-icon-wrapper .msa-icon-mask {
            width: 20px !important;
            height: 20px !important;
          }
          .mobile-text {
            position: relative;
            z-index: 3;
            display: block;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            line-height: 1.2;
            font-size: 14px;
          }
          .ps-btn--loading {
            position: relative;
            opacity: 0.7;
            pointer-events: none;
            transition: opacity 0.2s ease !important;
            overflow: hidden;
          }
          .ps-btn--loading::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 0;
            height: 100%;
            background: rgba(255, 255, 255, 0.2);
            z-index: 1;
            animation: slideRight 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
          }
          .ps-btn--loading::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 2px;
            background: rgba(255, 255, 255, 0.5);
            transform: translateX(-100%);
            z-index: 2;
            animation: lineSlideRight 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s forwards;
          }
          @keyframes slideRight {
            to {
              width: 100%;
            }
          }
          @keyframes lineSlideRight {
            to {
              transform: translateX(0);
            }
          }
          @media (max-width: 768px) {
            .ps-product__btn-group {
              gap: 8px;
            }
            .qv-figure {
              flex: 0 0 auto !important;
              width: auto !important;
            }
            .quantity-btn {
              flex: 0 0 28px;
              width: 28px;
            }
            .quantity-input {
              flex: 0 0 30px;
              width: 30px;
            }
            .qv-cart-btn {
              flex: 1 1 auto !important;
              width: auto !important;
              min-width: 0 !important;
              padding: 0 6px;
            }
            .qv-cart-btn .mobile-icon-wrapper,
            .qv-notify-btn .mobile-icon-wrapper {
              display: none;
            }
            .qv-cart-btn,
            .qv-notify-btn {
              overflow: visible;
              white-space: nowrap;
            }
            .mobile-text {
              font-size: 12px;
              white-space: nowrap;
              overflow: visible;
              text-overflow: clip;
            }
            .qv-actions {
              gap: 4px;
              flex: 0 0 auto !important;
              width: auto !important;
            }
            .qv-actions a {
              flex: 0 0 auto;
              width: auto;
            }
            .qv-actions a .msa-icon-mask {
              width: 28px !important;
              height: 28px !important;
            }
          }
          @media (max-width: 420px) {
            .ps-product__btn-group {
              gap: 6px;
            }
            .quantity-btn {
              flex: 0 0 26px;
              width: 26px;
            }
            .quantity-input {
              flex: 0 0 28px;
              width: 28px;
            }
            .qv-cart-btn {
              padding: 0 4px;
            }
            .mobile-icon-wrapper {
              margin-right: 6px;
            }
            .mobile-text {
              font-size: 11px;
            }
          }
        `}</style>
        {IconStyles}
      </div>
      {showNotifyModal && (
        <NotifyAvailabilityModal
          productId={activeProductId}
          productPrice={activeProductPrice}
          onClose={closeNotifyModal}
        />
      )}
      {loginPromptModal}
    </>
  );
};

export default connect((state) => state)(ModuleDetailShoppingActions);